import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { userService, USER_ID_KEY } from './services/userService';
import { UserProfile } from './types/user';
import { awardXp } from './utils/xp';
import './HomePage.css';

const QUIZZES = [
  { to: '/quiz',        icon: '🥋', title: 'Pensum Quiz',  desc: 'Test dit pensum bælteviden' },
  { to: '/Words',       icon: '🈶', title: 'Ord Quiz',     desc: 'Multiple-choice japanske ord' },
  { to: '/WrittenQuiz', icon: '✍️',  title: 'Skrive Quiz', desc: 'Skriv de japanske ord' },
];

const LOOKUPS = [
  { to: '/pensum',     icon: '📋', title: 'Pensum',      desc: 'Gennemse alle teknikker' },
  { to: '/WordSearch', icon: '🔍', title: 'Ord Søgning', desc: 'Søg i japansk-dansk ordbog' },
  { to: '/Kata',       icon: '🎌', title: 'Kata',        desc: 'Dan-niveau kata teknikker' },
];

const STREAK_BONUS_KEY = 'judoStreakBonusDate';

/**
 * Returns today's date as "YYYY-MM-DD" in UTC — matches how the API serialises
 * .NET's DateOnly (ISO-8601 date-only string).
 */
function utcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Computes the current streak length from an ordered list of "YYYY-MM-DD" strings.
 *
 * The streak represents consecutive *previous* days of training — today's session
 * keeps the streak alive but does not add to the count itself.
 *
 * Examples (today = May 17):
 *   [May 16, May 17]        → 1  (trained yesterday + today, streak = 1)
 *   [May 15, May 16, May 17]→ 2
 *   [May 16]                → 1  (trained yesterday, can still extend today)
 *   [May 17]                → 0  (first day, no previous chain yet)
 *   [May 14, May 15]        → 0  (streak broken — gap before today/yesterday)
 */
function computeStreak(days: string[]): number {
  if (days.length === 0) return 0;

  const daySet = new Set(days);

  const todayDate = new Date();
  todayDate.setUTCHours(0, 0, 0, 0);

  const yesterdayDate = new Date(todayDate);
  yesterdayDate.setUTCDate(yesterdayDate.getUTCDate() - 1);

  const todayStr     = todayDate.toISOString().slice(0, 10);
  const yesterdayStr = yesterdayDate.toISOString().slice(0, 10);

  // Streak is only alive if the user trained today or yesterday
  if (!daySet.has(todayStr) && !daySet.has(yesterdayStr)) return 0;

  // Always count backwards from yesterday — today's session is "in progress"
  // and doesn't contribute to the accumulated streak number
  let streak = 0;
  const cursor = new Date(yesterdayDate);

  while (daySet.has(cursor.toISOString().slice(0, 10))) {
    streak++;
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }

  return streak;
}

function HomePage() {
  const [user, setUser]     = useState<UserProfile | null>(null);
  const [streak, setStreak] = useState<number>(0);

  useEffect(() => {
    const userId = localStorage.getItem(USER_ID_KEY);
    if (!userId) return;

    // Load user profile and training days in parallel
    Promise.all([
      userService.getUser(userId),
      userService.getTrainingDays(userId),
    ]).then(([profile, history]) => {
      setUser(profile);

      const currentStreak = computeStreak(history.trainingDays);
      setStreak(currentStreak);

      // Award the streak bonus once per calendar day
      if (currentStreak > 0) {
        const today = utcToday();
        const lastBonus = localStorage.getItem(STREAK_BONUS_KEY);
        if (lastBonus !== today) {
          localStorage.setItem(STREAK_BONUS_KEY, today);
          awardXp(30);
        }
      }
    }).catch(() => {});
  }, []);

  const streakActive = streak > 0;

  return (
    <div className="homepage-container">
      {user && (
        <div className={`streak-banner${streakActive ? ' streak-banner--active' : ' streak-banner--inactive'}`}>
          <span className="streak-flame">{streakActive ? '🔥' : '💤'}</span>
          <div className="streak-text">
            <span className="streak-warning">
              {streakActive ? `${streak} dag${streak !== 1 ? 'es' : 's'} streak!` : 'Ingen aktiv streak'}
            </span>
            <span className="streak-sub">
              {streakActive
                ? 'Hold den ved lige — træn i dag!'
                : 'Træn i dag for at starte en streak'}
            </span>
          </div>
          <div className="streak-xp">
            <span className="streak-xp-value">{user.totalXp}</span>
            <span className="streak-xp-label">XP</span>
          </div>
        </div>
      )}

      <div className="homepage-logo">🥋</div>
      <h1 className="homepage-title">Judo<span>lingo</span></h1>
      <p className="homepage-subtitle">Lær judo — én teknik ad gangen</p>

      <div className="nav-section">
        <p className="nav-section-label">Quizzer</p>
        <div className="nav-grid">
          <Link to="/Judolingo" className="nav-card nav-card--featured">
            <span className="nav-card-icon">⚡</span>
            <div>
              <div className="nav-card-title">Judolingo</div>
              <div className="nav-card-desc">Blandet quiz — 35 spørgsmål, 3 modes</div>
            </div>
          </Link>
          {QUIZZES.map(({ to, icon, title, desc }) => (
            <Link key={to} to={to} className="nav-card">
              <span className="nav-card-icon">{icon}</span>
              <div className="nav-card-title">{title}</div>
              <div className="nav-card-desc">{desc}</div>
            </Link>
          ))}
        </div>
      </div>

      <div className="nav-section">
        <p className="nav-section-label">Opslagsværk</p>
        <div className="nav-grid">
          {LOOKUPS.map(({ to, icon, title, desc }) => (
            <Link key={to} to={to} className="nav-card">
              <span className="nav-card-icon">{icon}</span>
              <div className="nav-card-title">{title}</div>
              <div className="nav-card-desc">{desc}</div>
            </Link>
          ))}
        </div>
      </div>

      <Link to="/login" className="nav-card nav-card--profile">
        <span className="nav-card-icon">👤</span>
        <div>
          <div className="nav-card-title">Min Profil</div>
          <div className="nav-card-desc">XP, fremskridt og daglig træning</div>
        </div>
      </Link>

      <div className="info-notice">
        Billedmateriale tilhører Dansk Judo &amp; Ju-Jitsu Union (DJU).<br />
        © Dansk Judo &amp; Ju-Jitsu Union. Alle rettigheder forbeholdes.<br />
        <strong>Denne app er ikke officielt tilknyttet DJU.</strong>
      </div>
    </div>
  );
}

export default HomePage;
